import React from 'react';
import { useAppSelector, useAppDispatch } from '../app/hooks';
import { OrderingSelectors, OrderingOperations } from 'polygon-ordering';
import SubSection from './SubSection';
import { setDesiredDeliveryAddress } from '../slices/desiredDeliveryAddress';
import { DELIVERY_ADDRESS_TYPE } from '../libs/polygon-ordering/src/constants/deliveryAddress';
import { setSelectedAddress, updateForm } from '../slices/deliveryAddressForm';
import { useTranslation } from 'react-i18next';
import getThemeLookup from '../selectors/getThemeLookup';
import { TEXT_PROPERTIES } from '../utils/theme';
import RedcatMapPinHomeIcon from './icons/redcat/RedcatMapPinHomeIcon';
import combineStyles from '../utils/combineStyles';
import RedcatHistoryIcon from './icons/redcat/RedcatHistoryIcon';
import RedcatBriefcaseIcon from './icons/redcat/RedcatBriefcaseIcon';
import RedcatMapPinGroupIcon from './icons/redcat/RedcatMapPinGroupIcon';
import RedcatMapPinVariantIcon from './icons/redcat/RedcatMapPinVariantIcon';
import { cloneDeep } from 'lodash';

const { clearBuffer } = OrderingOperations;
const { getMember } = OrderingSelectors;

const SavedAddresses: React.FC = () => {
  const p = useAppSelector(getThemeLookup);
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const member = useAppSelector(getMember);

  // This is a fail safe to prevent malformed addresses from being used.
  // Specifically if they don't match the DeliveryAddress type.
  let effectiveDeliveryAddress = useAppSelector(state => state.desiredDeliveryAddress);
  if (!effectiveDeliveryAddress?.formattedAddress) {
    effectiveDeliveryAddress = null;
  }

  const recentAddresses = useAppSelector(state => state.recentLocations);

  const getAddressTitle = (address: DeliveryAddress) => {
    if (address.addressType === DELIVERY_ADDRESS_TYPE.HOME) {
      return t('deliveryAddress.addressTypeHome');
    }

    if (address.addressType === DELIVERY_ADDRESS_TYPE.WORK) {
      return t('deliveryAddress.addressTypeWork');
    }

    return address.locationName;
  };

  const getAddressIcon = (address: DeliveryAddress) => {
    const iconColor = p('savedAddressIcon', ['color']).color;

    if (!address.addressId) {
      return <RedcatHistoryIcon color={iconColor} />;
    }

    switch (address.addressType) {
      case DELIVERY_ADDRESS_TYPE.HOME:
        return <RedcatMapPinHomeIcon color={iconColor} />;
      case DELIVERY_ADDRESS_TYPE.WORK:
        return <RedcatBriefcaseIcon color={iconColor} />;
      case DELIVERY_ADDRESS_TYPE.FRIENDS_FAMILY:
        return <RedcatMapPinGroupIcon color={iconColor} />;
      case DELIVERY_ADDRESS_TYPE.OTHER:
      default:
        return <RedcatMapPinVariantIcon color={iconColor} />;
    }
  };

  const onAddressClicked = (address: DeliveryAddress) => {
    dispatch(setDesiredDeliveryAddress(address));
  };

  const onEditClicked = (address: DeliveryAddress) => {
    dispatch(
      updateForm({
        editingAddress: cloneDeep(address),
        page: 'DeliveryDetailsConfirmation',
      }),
    );
    dispatch(setSelectedAddress(address));
    dispatch(clearBuffer({}));
  };

  let addresses = [
    ...(member?.savedAddresses || []),
    ...recentAddresses.filter(address => address.placeId),
  ];

  if (
    effectiveDeliveryAddress &&
    !addresses.some(
      address =>
        (address.addressId && address.addressId === effectiveDeliveryAddress.addressId) ||
        (address.placeId && address.placeId === effectiveDeliveryAddress.placeId),
    )
  ) {
    addresses = [effectiveDeliveryAddress, ...addresses];
  }

  const isActive = (address: DeliveryAddress) => {
    if (effectiveDeliveryAddress?.addressId) {
      return effectiveDeliveryAddress.addressId === address.addressId;
    }

    if (address.addressId && !effectiveDeliveryAddress?.addressId) {
      return (
        address.addressType === effectiveDeliveryAddress?.addressType &&
        address.displayAddress.firstLine === effectiveDeliveryAddress?.displayAddress.firstLine &&
        address.displayAddress.secondLine === effectiveDeliveryAddress?.displayAddress.secondLine
      );
    }

    return effectiveDeliveryAddress?.placeId === address.placeId;
  };

  return (
    <div>
      {addresses.map((address, index) => (
        <div key={index}>
          <SubSection
            title={getAddressTitle(address)}
            titleStyle={combineStyles(
              p('savedAddressTitle', TEXT_PROPERTIES),
              isActive(address) && p('savedAddressTitleActive', TEXT_PROPERTIES),
            )}
            subtitle={`${address.displayAddress.firstLine} ${address.displayAddress.secondLine}`}
            subtitleStyle={p('savedAddressSubtitle', TEXT_PROPERTIES)}
            action={() => onAddressClicked(address)}
            leftIcon={() => getAddressIcon(address)}
            active={isActive(address)}
            rightSlot={() =>
              address.addressId ? (
                <div onClick={() => onEditClicked(address)}>
                  <span style={p('editSavedAddressButton', TEXT_PROPERTIES)}>
                    {t('deliveryAddress.editSavedDeliveryAddress')}
                  </span>
                </div>
              ) : null
            }
            rightSlotAction={() => onEditClicked(address)}
          />
        </div>
      ))}
    </div>
  );
};

export default SavedAddresses;
